import {
  ackSchema,
  bulkMessageActionResponseSchema,
  listMessagesResponseSchema,
  messageDetailSchema,
  messageSummarySchema,
  sendMessageResponseSchema,
  threadDetailSchema,
  type Ack,
  type BulkMessageActionRequest,
  type ForwardMessageRequest,
  type ListMessagesQuery,
  type ListMessagesResponse,
  type MessageDetail,
  type MessageId,
  type MessageSummary,
  type ReplyMessageRequest,
  type SendMessageRequest,
  type SendMessageResponse,
  type ThreadDetail,
  type ThreadId,
  type MailboxId,
  type LabelId,
  type UpdateMessageRequest,
} from "@mailflare/shared";

import { apiRequest, apiRequestText } from "@/lib/api-client";

export async function listMessages(
  mailboxId: MailboxId,
  params: Partial<ListMessagesQuery> = {},
): Promise<ListMessagesResponse> {
  return listMessagesResponseSchema.parse(
    await apiRequest<unknown>(`/mailboxes/${encodeURIComponent(mailboxId)}/messages`, {
      query: params,
    }),
  );
}

export async function getMessage(messageId: MessageId): Promise<MessageDetail> {
  return messageDetailSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}`),
  );
}

export async function updateMessage(
  messageId: MessageId,
  body: UpdateMessageRequest,
): Promise<MessageSummary> {
  return messageSummarySchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}`, {
      method: "PATCH",
      body,
    }),
  );
}

export async function deleteMessage(messageId: MessageId): Promise<Ack> {
  return ackSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}`, { method: "DELETE" }),
  );
}

export async function bulkMessageAction(
  body: BulkMessageActionRequest,
): Promise<{ readonly ok: true; readonly affected: number }> {
  return bulkMessageActionResponseSchema.parse(
    await apiRequest<unknown>("/messages/bulk", { method: "POST", body }),
  );
}

export async function sendMessage(body: SendMessageRequest): Promise<SendMessageResponse> {
  return sendMessageResponseSchema.parse(
    await apiRequest<unknown>("/messages/send", { method: "POST", body }),
  );
}

export async function replyToMessage(
  messageId: MessageId,
  body: ReplyMessageRequest,
): Promise<SendMessageResponse> {
  return sendMessageResponseSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}/reply`, {
      method: "POST",
      body,
    }),
  );
}

export async function forwardMessage(
  messageId: MessageId,
  body: ForwardMessageRequest,
): Promise<SendMessageResponse> {
  return sendMessageResponseSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}/forward`, {
      method: "POST",
      body,
    }),
  );
}

export async function getRawMessage(messageId: MessageId): Promise<string> {
  return apiRequestText(`/messages/${encodeURIComponent(messageId)}/raw`);
}

export async function addMessageLabel(messageId: MessageId, labelId: LabelId): Promise<Ack> {
  return ackSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}/labels`, {
      method: "POST",
      body: { labelId },
    }),
  );
}

export async function removeMessageLabel(messageId: MessageId, labelId: LabelId): Promise<Ack> {
  return ackSchema.parse(
    await apiRequest<unknown>(`/messages/${encodeURIComponent(messageId)}/labels`, {
      method: "DELETE",
      query: { labelId },
    }),
  );
}

export async function getThread(mailboxId: MailboxId, threadId: ThreadId): Promise<ThreadDetail> {
  return threadDetailSchema.parse(
    await apiRequest<unknown>(
      `/mailboxes/${encodeURIComponent(mailboxId)}/threads/${encodeURIComponent(threadId)}`,
    ),
  );
}
